<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { portalApi } from '@/api/Settings/PortalApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import LoadingSpinner from '@/components/Spinners/LoadingSpinner.vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { systemEchoes } from '@/composables/echo/useSystemEchoes.js'
import { api } from '@/api/ApiGateway.js'
import InputText from '@/components/Forms/InputText.vue'
import ListBox from '@/components/Forms/ListBox.vue'
import { ButtonSize } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { Format } from '@/models/utils/FormatUtils.js'

const props = defineProps({
	connectedUsers: { type: Object, default: null },
})
const conn_users = ref(props.connectedUsers)
watch(
	() => props.connectedUsers,
	(val) => { conn_users.value = val },
)

const isLoading = ref(false)
const isSaving = ref(false)
const toaster = new Toaster()
const showEchoTests = ref(false)

/**
 *
 * @type {UnwrapRef<*[{"setting": {Object}, "setting_name":string}]>}
 */
const settings = ref([])
const checkboxRefs = ref([])

onMounted(() => {
	loadSettings()
})

/**
 * Load scheduled report settings.
 */
function loadSettings()
{
	isLoading.value = true

	portalApi.loadSettings(onLoadComplete, "Failed to load settings.")
}


/**
 * Called after loading
 * @param _p_response Response from submission
 * @param p_data Response object from submission
 * @param p_error_eh True if there was an error
 */
function onLoadComplete(_p_response, p_data, p_error_eh)
{
	try
	{
		isLoading.value = false

		if (!p_error_eh)
		{
			//This will need to be updated to handle different types of settings in the future;
			settings.value = p_data.data;
		}
	}
	catch (e)
	{
		toaster.error(e.message)
		console.error(e)
	}
}

/**
 * Called after saving. Reloads the data.
 */
function onSaveComplete()
{
	isSaving.value = false
	loadSettings()
	toaster.success("Settings updated successfully.")
}

function settingChanged(p_setting)
{
	//Updates the checkboxes inside the div blocks;
	checkboxRefs.value.forEach((checkbox) => {
		if (checkbox.getInputId() === 'cbx_' + p_setting.setting_name) {
			checkbox.performClick()
		}
	})
}

/**
 * Save changes to the portal settings
 */
function save() {
	isSaving.value = true
	isLoading.value = true

	portalApi.updateSettings(settings.value, onSaveComplete, 'Failed to update setting.')
}


/**
 * Determines the total number of connected users across tenants. Must return a string b/c the popper :content expects one
 * and the value won't update if I don't.
 * @type {ComputedRef<string>}
 */
const totalConnectedUsers = computed(() => {
	let total = 0

	for (const tenant in conn_users.value)
	{
		total += conn_users.value[tenant].ids.size;
	}

	if (total === 0)
	{
		return 'Awaiting count...';
	}
	return total + '';
})

//#region System
function connectSystem()
{
	systemEchoes.systemConnect();
}
function disconnectSystem()
{
	systemEchoes.systemDisconnect();
}
function connectSystemMessage()
{
	systemEchoes.systemMessageConnect();
}
function disconnectSystemMessage()
{
	systemEchoes.systemMessageDisconnect();
}
//#endregion System

//#region Events
function connectSystemMessageEvent()
{
	systemEchoes.systemMessageEventConnect();
}
function stopSystemMessageEvent()
{
	systemEchoes.systemMessageEventStopListening();
}
function connectSystemUpdateEvent()
{
	systemEchoes.systemUpdateConnect();
}
function stopSystemUpdateEvent()
{
	systemEchoes.systemUpdateDisconnect();
}
//#endregion Events

//#region User
/**
 * Connect to the user channel and add events.
 */
function connectUserPresence()
{
	systemEchoes.userPresenceConnect()
}


/**
 * Leave user Channel
 */
function disconnectUserPresence()
{
	systemEchoes.userPresenceDisconnect();
}
//#endregion User

//#region Send System/Update Message
const system_message = ref('System Message Test')

/**
 * Triggers the send message function in the app service which sends to the
 * 'system.message' and 'system.update' events.
 */
function sendSystemMessage()
{
	api.post('system-message', { "message": system_message.value}).then((response) => {
		console.log('system-message sent', response);
		toaster.success("Sent: system.message");
	})
}
//#endregion Send System/Update Message

//#region Notifications
const notification_message = ref('Notification Message')
const selected_notification_user = ref({})
const role_id = ref(0)

const user_options = computed(() => {
	let items = [];

	for (const tenant in conn_users.value)
	{
		for (let i = 0; i < conn_users.value[tenant].users.length; i++)
		{
			const user = conn_users.value[tenant].users[i];
			items.push({ "id": user.id, text: conn_users.value[tenant].tenant.name + ": " + user.name})
		}
	}
	return items;
})

function connectNotificationUserChannel()
{
	systemEchoes.notificationUserConnect();
}

function disconnectNotificationUserChannel()
{
	systemEchoes.notificationUserDisconnect();
}

function connectNotificationRoleChannel()
{
	systemEchoes.notificationRoleConnect();
}

function disconnectNotificationRoleChannel()
{
	systemEchoes.notificationRoleDisconnect();
}

/**
 * Triggers the send message function in the app service which sends the
 * 'user.message' and 'user.update' events.
 */
function sendUserNotification()
{
	if (Format.int(selected_notification_user.value.id) > 0)
	{
		api.post('notification-user', { "user_id": selected_notification_user.value.id, "message": notification_message.value}).then((response) => {
			console.log('user-message sent', response);
			toaster.success("Sent: notification.user");
		})
	}
	else
	{
		toaster.error("Select a user first.")
	}
}
/**
 * Triggers the role notification function in the app service which sends the
 * 'notification.role' event.
 */
function sendRoleNotification()
{
	if (Format.int(role_id.value) > 0)
	{
		api.post('notification-role', { "role_id": Format.int(role_id.value), "message": notification_message.value }).then((response) =>
		{
			console.log('user-message sent', response);
			toaster.success("Sent: notification.role");
		})
	}
	else
	{
		toaster.error("Enter a valid role id.")
	}
}
//#endregion Notifications
</script>

<template>
	<div>
		<LoadingSpinner v-if="isLoading" />

		<div v-else>
			<div>
				<!-- Don't remove :content="totalConnectedUsers", the value in the pooper won't update without a computed value -->
				<Popper :content="totalConnectedUsers" hover arrow placement="bottom-start">
					<div class="flex flex-row gap-x-3">
						<div class="text-label font-bold" @click="showEchoTests = !showEchoTests">Connected Users:</div><div> {{totalConnectedUsers}}</div>
					</div>

					<template #content>
						<table v-if="Object.keys(conn_users).length > 0">
							<tbody>
							<tr v-for="(tenant, index) in conn_users" :key="index" >
								<td class="flex-1 pr-2 border border-grayscale-light border-opacity-40" >{{ tenant.tenant.name }}</td>
								<td class="w-12 border border-grayscale-light border-opacity-40 border-l-0">{{ tenant.ids.size }}</td>
							</tr>
							</tbody>
						</table>
						<div v-else>
							Awaiting user count from server, updated every minute.
						</div>
					</template>
				</Popper>
				<div v-if="showEchoTests" class="flex flex-row gap-3">

					<fieldset>
						<legend class="font-bold">Presence and System</legend>
						<div>
							<p class="font-bold">System</p>
							<div class="flex flex-row">
								<SubmitButton @click.prevent="connectSystem" label="Connect" :size="ButtonSize.Dev"/>
								<SubmitButton @click.prevent="disconnectSystem" label="Disconnect" :size="ButtonSize.Dev"/>
							</div>
							<p class="font-bold">Presence</p>
							<div class="flex flex-row">
								<SubmitButton @click.prevent="connectUserPresence" label="Connect User" :size="ButtonSize.Dev"/>
								<SubmitButton @click.prevent="disconnectUserPresence" label="Disconnect User" :size="ButtonSize.Dev"/>
							</div>
							<fieldset>
								<legend class="font-bold">System Message/Update</legend>
								<div>
									<p>These are two events under one 'system' channel <br/>(system.message, system.update).</p>
									<div class="flex flex-row">
										<SubmitButton @click.prevent="sendSystemMessage" label="Send Message/Update" :size="ButtonSize.Dev"/>
										<InputText v-model="system_message" customClass="!p-1 mt-2" ></InputText>
									</div>
									<SubmitButton @click.prevent="connectSystemMessage" label="Connect System Message Channel (both events)" :size="ButtonSize.Dev"/>
									<SubmitButton @click.prevent="disconnectSystemMessage" label="Disconnect System Message Channel" :size="ButtonSize.Dev"/>

									<fieldset>
										<legend class="font-bold">Events</legend>
										<div>
											<SubmitButton @click.prevent="connectSystemMessageEvent" label="Connect system.message Event" :size="ButtonSize.Dev"/>
											<SubmitButton @click.prevent="stopSystemMessageEvent" label="Stop system.message event" :size="ButtonSize.Dev"/>
											<SubmitButton @click.prevent="connectSystemUpdateEvent" label="Connect system.update event" :size="ButtonSize.Dev"/>
											<SubmitButton @click.prevent="stopSystemUpdateEvent" label="Stop system.update event" :size="ButtonSize.Dev"/>
										</div>
									</fieldset>
								</div>
							</fieldset>
						</div>
					</fieldset>

					<fieldset>
						<legend class="font-bold">Notifications</legend>
						<div>
							<label class="font-semibold">Message:</label>
							<InputText v-model="notification_message" customClass="!p-2 mt-2" ></InputText>
							<ListBox :items="user_options" label="Send To:" labelClass="font-semibold" inlineLabel="Select User" v-model="selected_notification_user" :showListBoxOutline="true"/>
							<fieldset>
								<legend class="font-bold">User Notification</legend>
								<div>
									<div class="flex flex-row ">
										<SubmitButton @click.prevent="connectNotificationUserChannel()" label="Connect" :size="ButtonSize.Dev"/>
										<SubmitButton @click.prevent="disconnectNotificationUserChannel" label="Disconnect" :size="ButtonSize.Dev"/>
									</div>
									<hr />
									<SubmitButton @click.prevent="sendUserNotification" label="Send User Notif" :size="ButtonSize.Dev"/>
								</div>
							</fieldset>
							<fieldset>
								<legend class="font-bold">Role Notification</legend>
								<div>
									<div class="flex flex-row ">
										<SubmitButton @click.prevent="connectNotificationRoleChannel()" label="Connect" :size="ButtonSize.Dev"/>
										<SubmitButton @click.prevent="disconnectNotificationRoleChannel" label="Disconnect" :size="ButtonSize.Dev"/>
									</div>
									<hr />
									<label class="font-semibold">Role ID:</label>
									<InputText v-model="role_id" customClass="!p-2 mt-2" ></InputText>
									<!--									<ListBox :items="role_options" label="Send To:" labelClass="font-bold" inlineLabel="Select User" v-model="selected_notification_role" :showListBoxOutline="true"/>-->
									<SubmitButton @click.prevent="sendRoleNotification" label="Send Role Notif" :size="ButtonSize.Dev"/>
								</div>
							</fieldset>
						</div>
					</fieldset>
				</div>
			</div>

			<div class="text-label font-bold text-lg mt-4">Settings:</div>
			<div class="flex flex-wrap flex-row gap-4 mt-2">
				<div
					class="flex h-full min-h-[100px] p-4 rounded border border-t-4 border-primary-darker hover:border-primary-bold-hover cursor-pointer"
					v-for="(setting, index) in settings"
					:key="index"
					@click="settingChanged(setting)"
				>
					<div class="flex flex-row items-center pointer-events-none">
						<CheckBox
							:input-id="'cbx_' + setting.setting_name"
							ref="checkboxRefs"
							v-model="setting.setting.enabled"
						/>
						<div class="bg-[#B2BBC9] h-full w-[1px] mx-4"/>
					</div>
					<span class="font-medium my-auto w-fit">{{ setting.setting_name }}</span>
				</div>
			</div>


		</div>

		<SubmitButton class="mt-6" label="Save Settings" @click="save" size="small" :disabled="isSaving" :loading="isSaving"/>
	</div>
</template>

<style scoped>
fieldset{
	@apply border border-grayscale border-opacity-50 p-2;
}
legend{
	@apply px-2;
}
fieldset button{
	@apply m-2;
}
</style>